// @flow
import * as React from 'react';
import { isDefined } from 'utils/functional';
import Box from 'components/Box';
import InfoBox from 'components/InfoBox';
import Link, { RouterLink } from 'components/Link';
import { FacebookButton, GoogleButton } from 'components/Button';
import { BodySmall } from 'components/Text';
import Divider from 'components/Divider';
import Form, { GeneralError, InputField, SubmitButton } from 'components/Form';
import { trackClicked } from 'utils/analytics';
import { deleteCookie } from 'utils/cookies';

const LoginForm = ({
  initialValues,
  thirdPartyLoginError,
  isModal,
  showSignupModal,
  getSignupUrl,
  signUpOverrideFn,
  showForgotPasswordModal,
  getForgotPasswordUrl,
  getThirdPartyLoginUrl,
  submitLogin,
  header,
  centered
}: {
  initialValues: Object,
  thirdPartyLoginError: ?string,
  isModal?: boolean,
  showSignupModal: Function,
  getSignupUrl: string => string | Object,
  signUpOverrideFn?: Function,
  showForgotPasswordModal: ({
    email: string,
    showUnsupportedServiceMessage?: boolean
  }) => void,
  getForgotPasswordUrl: ({
    email: string,
    showUnsupportedServiceMessage?: boolean
  }) => Object,
  getThirdPartyLoginUrl: ('facebook' | 'google') => string,
  submitLogin: Function,
  header?: React.Node,
  centered?: boolean
}) => {
  const SignupLink = ({ email, ...props }: { email: string }) => {
    if (signUpOverrideFn) {
      return (
        <Link
          href="#"
          onClick={e => {
            e.preventDefault();
            trackClicked('Sign Up');
            signUpOverrideFn();
          }}
          data-qa-id="signup-link"
          {...props}
        />
      );
    }
    if (isModal) {
      return (
        <Link
          href="#"
          onClick={e => {
            e.preventDefault();
            trackClicked('Sign Up');
            showSignupModal(email);
          }}
          data-qa-id="signup-link"
          {...props}
        />
      );
    }
    return (
      <RouterLink
        to={getSignupUrl(email)}
        onClick={() => {
          trackClicked('Sign Up');
        }}
        data-qa-id="signup-link"
        {...props}
      />
    );
  };

  const ForgotPasswordLink = ({
    email,
    showUnsupportedServiceMessage,
    ...props
  }: {
    email: string,
    showUnsupportedServiceMessage?: boolean
  }) => {
    if (isModal) {
      return (
        <Link
          href="#"
          onClick={e => {
            e.preventDefault();
            showForgotPasswordModal({ email, showUnsupportedServiceMessage });
          }}
          {...props}
        />
      );
    }
    return (
      <RouterLink
        to={getForgotPasswordUrl({ email, showUnsupportedServiceMessage })}
        {...props}
      />
    );
  };

  return (
    <Form
      data-qa-id="login-form"
      initialValues={initialValues}
      onSubmit={submitLogin}
      noScrollToError
      render={({ values: { email } }) => (
        <Box textAlign={centered ? 'center' : 'left'}>
          {isDefined(header) ? (
            header
          ) : (
            <BodySmall as="p" mt={3} mb={4}>
              {getText('Log in or create your account to get started.')}
            </BodySmall>
          )}

          {thirdPartyLoginError && (
            <InfoBox className="error" type="danger" mb={4}>
              {thirdPartyLoginError}
            </InfoBox>
          )}

          <Box
            display="flex"
            flexWrap="wrap"
            justifyContent={centered ? 'center' : 'flex-start'}
            mb={4}
            mt="-6px"
            mx="-6px"
          >
            <Box m="6px">
              <GoogleButton
                onClick={() =>
                  deleteCookie('userDismissedNotificationPreferencesDialog')
                }
                href={getThirdPartyLoginUrl('google')}
                data-qa-id="google-oauth"
              />
            </Box>

            <Box m="6px">
              <FacebookButton
                onClick={() =>
                  deleteCookie('userDismissedNotificationPreferencesDialog')
                }
                href={getThirdPartyLoginUrl('facebook')}
                data-qa-id="facebook-oauth"
              />
            </Box>
          </Box>

          <Divider my={24} />

          <GeneralError />

          <InputField
            name="email"
            type="email"
            data-modal-focus={isModal ? 'true' : null}
            placeholder={getText('casey.smith@example.com')}
            label={
              /* TRANSLATORS: Login form */
              getText('Email')
            }
          />

          <InputField
            name="password"
            type="password"
            label={
              /* TRANSLATORS: Login form */
              getText('Password')
            }
          />

          <Box mt={[16, null, 40]}>
            <SubmitButton width="100%" size="large">
              {/* TRANSLATORS: log in button */ getText('Log in')}
            </SubmitButton>
          </Box>

          <Box mt={[16, null, 32]}>
            <BodySmall textAlign="center">
              <Box>
                {getText("Don't have an account?")}{' '}
                <SignupLink email={email}>{getText('Sign up')}</SignupLink>
              </Box>
              <Box mt="8px">
                <ForgotPasswordLink mt={2} email={email}>
                  {getText('I forgot my password')}
                </ForgotPasswordLink>
              </Box>
              <Box mt="8px">
                <ForgotPasswordLink showUnsupportedServiceMessage email={email}>
                  {getText('Looking for Yahoo, Microsoft, Twitter login?')}
                </ForgotPasswordLink>
              </Box>
            </BodySmall>
          </Box>
        </Box>
      )}
    />
  );
};

export default LoginForm;
